import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { AddRounded, CompareArrowsRounded, HistoryOutlined, MoreVert } from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { tooltipStyleProps } from "services/constants/Styles";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { detailCardSx } from "components/DesignSystem/RequestDetailField";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { designColors, designRadii, designShell, designType, designWeights } from "services/constants/designTokens";
import { Field, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { DateField, SelectField, TextField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { PayInputAuditTrail } from "pages/PayRoll/PayInputs/Shared/AuditTrail";
import { EmptyRow, Required, SimpleTable, tableCellSx } from "pages/PayRoll/Salary/Shared/salaryUi";
import { FormulaChip } from "pages/PayRoll/Shared/statutoryUi";
import {
  ACTIVE_STATUS_COLORS,
  VERSION_STATUS_COLORS,
  activeVersion,
  formatDay,
  isLocked,
  versionRange,
} from "pages/PayRoll/Shared/statutoryConfigData";

// Screen parts shared by the statutory Configuration tabs (PF, ESI, later
// PT): version header, version form, component tables and Version History.
// Each statutory item only supplies its own fields and rules.

const clone = (value) => JSON.parse(JSON.stringify(value));

// White section card: title, one-line info, then the content.
export const SettingsCard = ({ title, info, action, children, sx }) => (
  <Mui.Box sx={{ ...detailCardSx, p: { xs: 2, sm: 2.5 }, ...sx }}>
    <Mui.Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
      <Mui.Box sx={{ minWidth: 0 }}>
        <Mui.Typography component="h2" sx={designType.sectionHeading}>{title}</Mui.Typography>
        {info ? <Mui.Typography sx={{ ...designType.helperText, mt: 0.25 }}>{info}</Mui.Typography> : null}
      </Mui.Box>
      {action}
    </Mui.Stack>
    {children}
  </Mui.Box>
);

// Items get minWidth 0 so a column can shrink below its inputs' natural
// width; otherwise nested 1fr grids push the page into a sideways scroll.
export const SettingsGrid = ({ columns, children, sx }) => (
  <Mui.Box sx={{ display: "grid", gridTemplateColumns: columns, columnGap: 2, rowGap: 2, "& > *": { minWidth: 0 }, ...sx }}>{children}</Mui.Box>
);

// Selectable card with a tick box (e.g. Main code / Branch sub-code).
export const ChoiceCard = ({ selected, disabled, label, hint, onClick }) => (
  <Mui.ButtonBase
    onClick={onClick}
    disabled={disabled}
    sx={{
      flex: 1,
      justifyContent: "flex-start",
      gap: 1,
      px: 1.5,
      py: 1.25,
      borderRadius: designRadii.card,
      border: `1px solid ${selected ? designColors.teal : designColors.slate300}`,
      backgroundColor: selected ? designColors.tealTint : designColors.white,
      opacity: disabled && !selected ? 0.6 : 1,
      textAlign: "left",
    }}
  >
    <Mui.Checkbox size="small" checked={selected} disabled={disabled} tabIndex={-1} sx={{ p: 0, "&.Mui-checked": { color: designColors.tealDark } }} />
    <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: selected ? designWeights.semibold : designWeights.medium }}>
      {label}
      {hint ? <Mui.Box component="span" sx={{ ...designType.captionText, ml: 0.5 }}>({hint})</Mui.Box> : null}
    </Mui.Typography>
  </Mui.ButtonBase>
);

// Page header block: the active version with Version History and Create New
// Version (Open Draft once a draft exists). extra: more buttons before
// Version History (e.g. LWF's Copy from site).
// after: a button at the end of the card (e.g. "View ESI Eligibility").
export const ActiveVersionCard = ({ version, hasDraft, onHistory, onCreate, extra, after }) =>
  version ? (
    <Mui.Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={2}
      useFlexGap
      flexWrap="wrap"
      alignItems={{ xs: "stretch", sm: "center" }}
      sx={{ ...designShell.sectionCard, px: 2, py: 1.25, maxWidth: "100%" }}
    >
      <Mui.Box sx={{ minWidth: 0 }}>
        <Mui.Typography sx={designType.captionText}>Active Version</Mui.Typography>
        <Mui.Stack direction="row" spacing={1} alignItems="center">
          <Mui.Typography sx={{ fontSize: 15, fontWeight: designWeights.semibold, color: designColors.slate900, whiteSpace: "nowrap" }}>
            {version.name} ({formatDay(version.effective_from)})
          </Mui.Typography>
          <RequestStatusChip status={version.status} colorCode={VERSION_STATUS_COLORS[version.status]} />
        </Mui.Stack>
      </Mui.Box>
      <Mui.Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {extra}
        <SecondaryButton startIcon={<HistoryOutlined />} onClick={onHistory} sx={{ whiteSpace: "nowrap" }}>
          Version History
        </SecondaryButton>
        <PrimaryButton startIcon={<AddRounded />} onClick={onCreate} sx={{ whiteSpace: "nowrap" }}>
          {hasDraft ? "Open Draft" : "Create New Version"}
        </PrimaryButton>
        {after}
      </Mui.Stack>
    </Mui.Stack>
  ) : null;

// ---- Version form ----

// State and actions for one version's form: edit, save (a draft becomes the
// active version and the old one ends the day before), cancel, discard.
// validate(form, versions) -> { field: message }.
// persist (optional, for screens on an API): { save(form, { isDraft }),
// discard(form) } returning promises. The screen reloads its versions after
// them; a rejected save shows err.message and err.fieldErrors under the
// fields. Without persist, changes stay in memory (sample-data screens).
export const useVersionForm = ({ versions, setVersions, selectedId, setSelectedId, validate, persist }) => {
  const selected = versions.find((version) => version.id === selectedId) || activeVersion(versions);
  const [form, setForm] = useState(() => clone(selected));
  const [showErrors, setShowErrors] = useState(false);
  const [serverErrors, setServerErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Back to the saved values whenever another version is opened or saved.
  useEffect(() => {
    setForm(clone(selected));
    setShowErrors(false);
    setServerErrors({});
  }, [selected]);

  const locked = isLocked(form);
  const isDraft = form.status === "Draft";
  const dirty = JSON.stringify(form) !== JSON.stringify(selected);
  const errors = useMemo(() => validate(form, versions), [validate, form, versions]);
  const active = activeVersion(versions.filter((version) => version.status !== "Draft"));

  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }));
  const setComponents = (update) => setForm((current) => ({ ...current, components: update(current.components) }));

  const save = async () => {
    setShowErrors(true);
    if (Object.keys(errors).length) {
      showToast("error", errors.components || "Fix the highlighted fields before saving.");
      return;
    }
    if (persist) {
      setSaving(true);
      setServerErrors({});
      try {
        await persist.save(form, { isDraft });
      } catch (err) {
        setServerErrors(err.fieldErrors || {});
        showToast("error", err.message || "Couldn't save. Please try again.");
      } finally {
        setSaving(false);
      }
      return;
    }
    const now = moment().format("YYYY-MM-DDTHH:mm:ss");
    setVersions((list) =>
      list.map((version) => {
        if (version.id === form.id) {
          return { ...form, status: isDraft ? "Active" : form.status, created_by: form.created_by || "You", created_at: form.created_at || now };
        }
        if (isDraft && version.status === "Active") {
          return { ...version, status: "Superseded", effective_to: moment(form.effective_from).subtract(1, "day").format("YYYY-MM-DD") };
        }
        return version;
      }),
    );
    showToast(
      "success",
      isDraft ? `${form.name} is active from ${formatDay(form.effective_from)}.` : `${form.name} saved. Payrolls from ${formatDay(form.effective_from)} use it.`,
    );
  };

  const discard = async () => {
    if (persist) {
      setSaving(true);
      try {
        await persist.discard(form);
      } catch (err) {
        showToast("error", err.message || "Couldn't discard the draft. Please try again.");
      } finally {
        setSaving(false);
      }
      return;
    }
    setVersions((list) => list.filter((version) => version.id !== form.id));
    setSelectedId(active.id);
    showToast("success", "Draft discarded.");
  };

  return {
    form,
    set,
    setComponents,
    errors,
    error: (key) => (showErrors ? errors[key] || serverErrors[key] : undefined),
    locked,
    isDraft,
    dirty,
    active,
    saving,
    save,
    reset: () => setForm(clone(selected)),
    discard,
  };
};

// Banner above the form: locked / draft / applies-from.
export const VersionStatusBanner = ({ form, active, locked, isDraft, itemLabel, onOpenActive }) =>
  locked ? (
    <RequestInfoBanner
      tone="warning"
      title={`${form.name} is locked.`}
      body={`Used by payroll ${(form.used_by || []).join(", ") || "runs"}, so past months never change. Create a new version to change ${itemLabel} settings.`}
      action={
        form.id !== active?.id ? (
          <SecondaryButton onClick={onOpenActive} sx={{ whiteSpace: "nowrap" }}>Open Active Version</SecondaryButton>
        ) : null
      }
    />
  ) : isDraft ? (
    <RequestInfoBanner
      tone="purple"
      title={active ? `Draft — copied from ${active.name}.` : "Draft — first version."}
      body={
        active
          ? `Change what you need and pick the month it starts. Saving makes it the active version from that month; ${active.name} ends the day before.`
          : "Fill in the settings and pick the month it starts. Saving makes it the active version from that month."
      }
    />
  ) : (
    <RequestInfoBanner
      title={`Applies from ${formatDay(form.effective_from)} for payrolls not yet finalised.`}
      body="Once a payroll uses this version it is locked. To change it after that, create a new version from a new month."
    />
  );

export const VersionDetailsCard = ({ form, locked, isDraft, set, error }) => (
  <SettingsCard title="Version Details">
    <SettingsGrid columns={{ xs: "1fr", md: "2fr 2fr 1fr" }}>
      <Field label={<>Version Name<Required /></>} error={error("name")}>
        <TextField value={form.name} onChange={set("name")} disabled={locked} error={error("name")} />
      </Field>
      <Field label={<>Effective From<Required /> (1st of a month)</>} error={error("effective_from")}>
        <DateField value={form.effective_from} onChange={set("effective_from")} disabled={locked || !isDraft} error={error("effective_from")} />
      </Field>
      <Field label="Status">
        <Mui.Box sx={{ minHeight: 40, display: "flex", alignItems: "center" }}>
          <RequestStatusChip status={form.status} colorCode={VERSION_STATUS_COLORS[form.status]} />
        </Mui.Box>
      </Field>
      <Mui.Box sx={{ gridColumn: "1 / -1" }}>
        <Field label="Description">
          <TextField value={form.description} onChange={set("description")} disabled={locked} placeholder="What changed and why" />
        </Field>
      </Mui.Box>
    </SettingsGrid>
  </SettingsCard>
);

// Cancel / Save (Save & Activate for a draft) and Discard Draft.
export const VersionFooter = ({ form, active, locked, isDraft, dirty, saving, onCancel, onSave, onDiscard }) => {
  const [discardOpen, setDiscardOpen] = useState(false);
  if (locked) return null;
  return (
    <>
      <Mui.Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} justifyContent="flex-end" alignItems={{ sm: "center" }}>
        {isDraft ? (
          <Mui.Button onClick={() => setDiscardOpen(true)} sx={{ color: "#DC2626", textTransform: "none", fontWeight: designWeights.semibold, mr: { sm: "auto" } }}>
            Discard Draft
          </Mui.Button>
        ) : null}
        <SecondaryButton onClick={onCancel} disabled={!dirty || saving}>
          Cancel
        </SecondaryButton>
        <PrimaryButton onClick={onSave} disabled={!dirty && !isDraft} loading={Boolean(saving)}>
          {isDraft ? "Save & Activate" : "Save"}
        </PrimaryButton>
      </Mui.Stack>
      <GlobalConfirmDialog
        open={discardOpen}
        title="Discard draft"
        onCancel={() => setDiscardOpen(false)}
        onConfirm={() => {
          setDiscardOpen(false);
          onDiscard();
        }}
        confirmLabel="Discard"
        cancelLabel="Keep Draft"
        maxWidth="sm"
        danger
      >
        Discard {form.name}? {active?.name} stays the active version.
      </GlobalConfirmDialog>
    </>
  );
};

// ---- Component tables ----

// One table of components (calculation or contribution) with Edit / View
// and a menu to activate, deactivate or delete. Required codes can't be
// switched off or deleted.
// formulaOf(row): the formula text shown in the table. Without onAdd there
// is no Add Component button (e.g. LWF's fixed contribution components).
// showMenu={false} hides the ⋮ menu (status is then changed in the drawer).
// Fixed widths so the table fits the card: Formula is cut off with "…"
// (full text on hover), "Used for" takes the remaining width and wraps.
export const COMPONENT_TABLE_HEADS = [
  { label: "#", width: 48 },
  { label: "Component Name", width: "18%" },
  { label: "Component Code", width: "15%" },
  { label: "Formula", width: "26%" },
  { label: "Used for" },
  { label: "Status", width: 110 },
  { label: "Actions", width: 130 },
];
export const ComponentsTable = ({ title, info, kind, components, locked, requiredCodes, formulaOf, versionName, onAdd, onOpen, onChange, showMenu = true }) => {
  const [menu, setMenu] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const rows = components.filter((component) => component.kind === kind);
  const target = menu ? rows.find((row) => row.code === menu.code) : null;
  return (
    <SettingsCard
      title={`${title} (${rows.length})`}
      info={info}
      action={
        onAdd ? (
          <SecondaryButton startIcon={<AddRounded />} onClick={onAdd} disabled={locked} sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
            Add Component
          </SecondaryButton>
        ) : null
      }
    >
      <SimpleTable
        fixed
        minWidth={820}
        heads={COMPONENT_TABLE_HEADS}
      >
        {rows.length ? (
          rows.map((row, index) => (
            <Mui.TableRow key={row.code} hover sx={{ opacity: row.status === "Active" ? 1 : 0.6 }}>
              <Mui.TableCell sx={tableCellSx}>{index + 1}</Mui.TableCell>
              <Mui.TableCell sx={{ ...tableCellSx, fontWeight: designWeights.semibold }}>{row.name}</Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>{row.code}</Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>
                <FormulaChip formula={formulaOf(row)} />
              </Mui.TableCell>
              <Mui.TableCell sx={{ ...tableCellSx, color: designColors.slate600 }}>{row.used_for || "–"}</Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>
                <RequestStatusChip status={row.status} colorCode={ACTIVE_STATUS_COLORS[row.status]} />
              </Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>
                <Mui.Stack direction="row" spacing={0.5} alignItems="center">
                  <SecondaryButton onClick={() => onOpen(row)} sx={{ minWidth: 0, minHeight: 32, px: 1.5 }}>
                    {locked || row.fixed ? "View" : "Edit"}
                  </SecondaryButton>
                  {showMenu ? (
                    <Mui.Tooltip title="More actions" arrow slotProps={tooltipStyleProps}>
                      <span>
                        <Mui.IconButton
                          size="small"
                          aria-label={`More actions for ${row.name}`}
                          disabled={locked || requiredCodes.has(row.code)}
                          onClick={(event) => setMenu({ anchor: event.currentTarget, code: row.code })}
                        >
                          <MoreVert fontSize="small" />
                        </Mui.IconButton>
                      </span>
                    </Mui.Tooltip>
                  ) : null}
                </Mui.Stack>
              </Mui.TableCell>
            </Mui.TableRow>
          ))
        ) : (
          <EmptyRow colSpan={7} text="No components yet." />
        )}
      </SimpleTable>
      <Mui.Menu anchorEl={menu?.anchor} open={Boolean(target)} onClose={() => setMenu(null)}>
        <Mui.MenuItem
          onClick={() => {
            onChange((list) => list.map((item) => (item.code === target.code ? { ...item, status: item.status === "Active" ? "Inactive" : "Active" } : item)));
            setMenu(null);
          }}
        >
          {target?.status === "Active" ? "Deactivate" : "Activate"}
        </Mui.MenuItem>
        <Mui.MenuItem
          sx={{ color: "#DC2626" }}
          onClick={() => {
            setDeleteTarget(target);
            setMenu(null);
          }}
        >
          Delete
        </Mui.MenuItem>
      </Mui.Menu>
      <GlobalConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete component"
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          onChange((list) => list.filter((item) => item.code !== deleteTarget.code));
          setDeleteTarget(null);
        }}
        confirmLabel="Delete"
        cancelLabel="Keep"
        maxWidth="sm"
        danger
      >
        Delete {deleteTarget?.name} ({deleteTarget?.code}) from {versionName}? Formulas that use it will show an error.
      </GlobalConfirmDialog>
    </SettingsCard>
  );
};

// ---- Version History ----

// fields: [[key, label, show?]] compared between versions.
// formulaOf(component): the formula text shown for a component.
// Lists and objects (e.g. LWF bands) are compared by content.
const comparable = (value) => (value != null && typeof value === "object" ? JSON.stringify(value) : String(value ?? ""));
const changesBetween = (before, after, fields, formulaOf) => {
  if (!before) return [];
  const changes = fields
    .filter(([key]) => comparable(before[key]) !== comparable(after[key]))
    .map(([key, label, show = (value) => value]) => ({ label, from: show(before[key]), to: show(after[key]) }));
  after.components.forEach((component) => {
    const old = before.components.find((item) => item.code === component.code);
    if (!old) changes.push({ label: `${component.code} added`, value: formulaOf(component) });
    else if (formulaOf(old) !== formulaOf(component)) changes.push({ label: component.code, from: formulaOf(old), to: formulaOf(component) });
    else if (old.status !== component.status) changes.push({ label: component.code, from: old.status, to: component.status });
  });
  before.components
    .filter((component) => !after.components.some((item) => item.code === component.code))
    .forEach((component) => changes.push({ label: `${component.code} removed`, value: component.name }));
  return changes;
};

const CompareVersions = ({ versions, fields, formulaOf }) => {
  const [leftId, setLeftId] = useState(() => String(versions[Math.max(0, versions.length - 2)]?.id));
  const [rightId, setRightId] = useState(() => String(versions[versions.length - 1]?.id));
  const left = versions.find((version) => String(version.id) === leftId);
  const right = versions.find((version) => String(version.id) === rightId);
  const options = versions.map((version) => ({ value: String(version.id), label: `${version.name} · ${version.status}` }));
  const rows = left && right ? changesBetween(left, right, fields, formulaOf) : [];
  return (
    <SettingsCard title="Compare versions" info="Only the settings that differ are listed." sx={{ boxShadow: "none" }}>
      <SettingsGrid columns={{ xs: "1fr", sm: "1fr 1fr" }} sx={{ mb: 1.5 }}>
        <Field label="From">
          <SelectField value={leftId} onChange={(value) => value && setLeftId(value)} options={options} />
        </Field>
        <Field label="To">
          <SelectField value={rightId} onChange={(value) => value && setRightId(value)} options={options} />
        </Field>
      </SettingsGrid>
      <SimpleTable
        fit
        heads={[
          { label: "Setting", width: 170 },
          { label: left?.name || "From", width: 160 },
          { label: right?.name || "To", width: 160 },
        ]}
      >
        {leftId === rightId ? (
          <EmptyRow colSpan={3} text="Pick two different versions." />
        ) : rows.length ? (
          rows.map((row) => (
            <Mui.TableRow key={row.label}>
              <Mui.TableCell sx={{ ...tableCellSx, fontWeight: designWeights.semibold }}>{row.label}</Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>{row.from ?? "–"}</Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>{row.to ?? row.value ?? "–"}</Mui.TableCell>
            </Mui.TableRow>
          ))
        ) : (
          <EmptyRow colSpan={3} text="No differences." />
        )}
      </SimpleTable>
    </SettingsCard>
  );
};

export const VersionHistoryPanel = ({ open, versions, selectedId, fields, formulaOf, subtitle, note, onClose, onOpenVersion }) => {
  const [comparing, setComparing] = useState(false);
  useEffect(() => {
    if (open) setComparing(false);
  }, [open]);
  const saved = versions.filter((version) => version.status !== "Draft");
  const draft = versions.find((version) => version.status === "Draft");
  const entries = saved
    .map((version, index) => ({
      id: version.id,
      title: `${version.name} · ${versionRange(version)}`,
      at: version.created_at,
      actor: { name: version.created_by || "–" },
      tags: [{ label: version.status, tone: version.status === "Active" ? "green" : "slate" }],
      changes: changesBetween(saved[index - 1], version, fields, formulaOf),
      details: [{ label: "Used by payroll", value: (version.used_by || []).join(", ") || "Not used yet" }],
      note: version.description,
    }))
    .reverse();

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="Version History"
      subtitle={subtitle}
      bodySx={panelBodySx}
      footer={
        <Mui.Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ width: "100%" }}>
          {versions.length > 1 ? (
            <SecondaryButton startIcon={<CompareArrowsRounded />} onClick={() => setComparing((value) => !value)}>
              {comparing ? "Hide comparison" : "Compare versions"}
            </SecondaryButton>
          ) : null}
          <PrimaryButton onClick={onClose}>Close</PrimaryButton>
        </Mui.Stack>
      }
    >
      <Mui.Stack spacing={2}>
        {comparing ? <CompareVersions versions={versions} fields={fields} formulaOf={formulaOf} /> : null}
        <RequestInfoBanner body={note} />
        <SimpleTable
          fit
          heads={[
            { label: "Version", width: 110 },
            { label: "Range", width: 200 },
            { label: "Status", width: 100 },
            { label: "Used by payroll", width: 180 },
            { label: "", width: 80 },
          ]}
        >
          {[...versions].reverse().map((version) => (
            <Mui.TableRow key={version.id} selected={version.id === selectedId}>
              <Mui.TableCell sx={{ ...tableCellSx, fontWeight: designWeights.semibold }}>{version.name}</Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>{versionRange(version)}</Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>
                <RequestStatusChip status={version.status} colorCode={VERSION_STATUS_COLORS[version.status]} />
              </Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>{(version.used_by || []).join(", ") || "Not used yet"}</Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>
                <SecondaryButton onClick={() => onOpenVersion(version.id)} sx={{ minWidth: 0, minHeight: 32, px: 1.5 }}>
                  {isLocked(version) ? "View" : "Open"}
                </SecondaryButton>
              </Mui.TableCell>
            </Mui.TableRow>
          ))}
        </SimpleTable>
        {draft ? (
          <Mui.Typography sx={designType.helperText}>
            {draft.name} is a draft from {versionRange(draft).split(" →")[0]}. It shows here once it is saved.
          </Mui.Typography>
        ) : null}
        <Mui.Typography sx={{ ...designType.sectionHeading, pt: 1 }}>What changed</Mui.Typography>
        <PayInputAuditTrail entries={entries} />
      </Mui.Stack>
    </GlobalSidePanel>
  );
};
